import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isEqual, isNil } from 'lodash'
import classNames from 'classnames'
import { IconInfoCircle } from '@tabler/icons-react'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import Select from '@/components/forms/Select'
import { TextInput } from '@/components/forms/TextInput'
import { TextArea } from '@/components/forms/TextArea'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { toast } from '@/components/toasts/toast.helpers'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { fetchTaxonomyOptions, verifyMetadataField } from '@/reducers/playbooks/playbook.actions'

import { OptionsEditor } from '@/components/forms/OptionsEditor/OptionsEditor'
import {
  CATEGORIZATION_MAX_OPTIONS,
  LOGIC_COMBINATOR,
  LOGIC_FIELD_TYPE,
  PICK_LIST_MAX_OPTIONS,
  PICK_LIST_TYPE_KEY,
  getOptionsError,
  getDisplayTypeLabel,
  resolveDisplayTypeKey,
  resolveFieldTypeKey,
  normalizeFieldKey,
} from '../helpers'
import { FieldTypePill } from './FieldTypePill'
import { LogicCombinator } from './LogicCombinator'

const FIELD_TYPE = {
  EXTRACTION: 'extraction',
  PICK_LIST: 'pick_list',
  CATEGORIZATION: 'categorization',
  VOIP_METADATA: 'voip_metadata',
  LOGIC: LOGIC_FIELD_TYPE,
}

// Wire values for the summary service's BitFieldDataType enum.
const DATA_TYPE = {
  STRING: 'string',
  INT: 'integer',
  // A metadata value the provider sends can be fractional, so a numeric
  // voip_metadata field is a float — integer would fail validation on "3.5".
  FLOAT: 'float',
  BOOL: 'boolean',
}

// Extract-a-value tooltips, one per data_type.
const TEXT_TOOLTIP = 'Extract a value from the transcript based on the description.'
const NUMBER_TOOLTIP = 'Extract a number from the transcript based on the description'
const YESNO_TOOLTIP = 'Always respond with either yes or no to the question in the description'

const VOIP_METADATA_TOOLTIP =
  "Read a value straight off the call's VoIP metadata - no AI, no transcript."

const LOGIC_TOOLTIP =
  'Combine conditions on other fields into a yes or no answer - no AI, no transcript.'

// A logic field's conditions are added on its row once it exists, from the same
// branch button that gates any other field.
const LOGIC_CONDITIONS_HINT =
  'After adding the field, use its branch button to add the conditions it combines.'

// The metadata key is read out of the field's description (the backend resolves
// it as a dotted path against the call's voip_metadata JSON), so this type
// swaps the description input for a key input.
const VOIP_METADATA_KEY_HINT =
  'The key to read from the call\'s VoIP metadata. Use dots for a nested value, e.g. "caller.region".' // prettier-ignore

// A failed verification names the call it ran against, so the user can inspect
// that call's metadata. A check that never ran (no call to test on) has none.
const getVerifyErrorContent = ({ error, callId }) => {
  // Backend errors arrive unpunctuated, so end the sentence before the advisory
  // that follows it.
  const reason = error.endsWith('.') ? error : `${error}.`
  const suffix = 'You can add the field anyway.'
  if (isNil(callId)) {
    return `${reason} ${suffix}`
  }
  return `Tested on call ${callId}: ${reason} ${suffix}`
}

// How an org default field maps onto this modal's controls. A field name is its
// identity org-wide, so a name that already exists brings its type with it —
// the same reason the dropdown blocks reusing a name under a different type.
const resolveDefaultFieldControls = (defaultField) => {
  switch (defaultField.field_type) {
    case FIELD_TYPE.PICK_LIST:
    case FIELD_TYPE.CATEGORIZATION:
      return { fieldType: defaultField.field_type, dataType: DATA_TYPE.STRING, voipPicklist: false }
    case FIELD_TYPE.LOGIC:
      return { fieldType: FIELD_TYPE.LOGIC, dataType: DATA_TYPE.BOOL, voipPicklist: false }
    case FIELD_TYPE.VOIP_METADATA:
      return {
        fieldType: FIELD_TYPE.VOIP_METADATA,
        // A metadata field keeps float as float — a provider's value can be
        // fractional — and its Pick List data type is the taxonomy, not a type.
        dataType: defaultField.data_type || DATA_TYPE.STRING,
        voipPicklist: !isNil(defaultField.taxonomy_uuid),
      }
    default:
      return {
        fieldType: FIELD_TYPE.EXTRACTION,
        // An extraction field offers whole numbers only, so a float default
        // reads as Number — the same collapse the display type makes.
        dataType:
          defaultField.data_type === DATA_TYPE.FLOAT
            ? DATA_TYPE.INT
            : defaultField.data_type || DATA_TYPE.STRING,
        voipPicklist: false,
      }
  }
}

// The type an existing field name has to match to be reusable. A metadata field
// with the Pick List data type resolves its value against options, so it reuses
// Picklist names even though the field itself stays a string.
const resolveSelectedTypeKey = (fieldType, dataType, voipPicklist) =>
  voipPicklist ? PICK_LIST_TYPE_KEY : resolveFieldTypeKey(fieldType, dataType)

// A per-data-type hint on the description field, surfaced via an info icon so
// the description is phrased right for the type.
const DESCRIPTION_HINT = {
  integer: 'The number extracted will contain no decimal places',
  boolean: 'Ask a simple question, or explain what qualifies for each case',
}

// Guided creator for a new Notetaker field, shared by the playbook editor and
// the org defaults editor. Extraction is a plain free-text
// field; a "Pick List" resolves the answer against a taxonomy defined here —
// names only (larger cap) persists as a pick_list bit type, names +
// descriptions (smaller cap) as a categorization bit type. A "VoIP Metadata"
// field runs no prompt at all — the backend reads the call's voip_metadata JSON
// at the key named here (which travels in the field's description) and
// validates it against the chosen data type; its Pick List data type stays a
// string and carries options like a pick_list field. A "Logic" field runs no
// prompt either: it's a Yes/No that combines the conditions later added to it,
// and its description carries the combinator (all/any). It's only offered where
// conditions can be added (allowLogic), since without them it can't be saved.
// Choosing a name that already exists as an org default starts the field from
// that default: its type comes with the name (a name is a field's identity
// org-wide, so the type isn't a separate choice), its description prefills, and
// an option-backed one adopts the default's taxonomy instead of minting a second
// copy of the same list — so it resolves against those options like any inherited
// field. Editing the options abandons that adoption: an edited list is this
// configuration's own, and gets a taxonomy of its own on save.
//
// onCreate receives { key, value, fieldType, dataType, options, taxonomyUuid };
// the caller either links the field to taxonomyUuid or stages the options for
// the save path to mint a taxonomy from.
export const AddFieldModal = ({
  show,
  onClose,
  onCreate,
  fieldNameOptions = [],
  usedFieldNames = [],
  defaultFieldsByFieldName = {},
  playbookCid,
  organizationId,
  allowLogic = false,
}) => {
  const dispatch = useDispatch()
  // Bumped whenever the form is reset (including on close), so a verification
  // that resolves after that is dropped instead of reporting on a config the
  // user has already walked away from.
  const verifyRunRef = useRef(0)
  const [fieldType, setFieldType] = useState(FIELD_TYPE.EXTRACTION)
  const [dataType, setDataType] = useState(DATA_TYPE.STRING)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [options, setOptions] = useState([])
  // Whether a voip_metadata field resolves its value against an option list.
  // It's not a data_type of its own (the field stays a string, like a pick_list),
  // so it can't be inferred from dataType.
  const [voipPicklist, setVoipPicklist] = useState(false)
  // A metadata field is dry-run against a real call before it's added. Holding
  // the failure here (rather than blocking) is what lets the user add it anyway:
  // a second click with the error already shown goes through. { error, callId } —
  // callId is absent when the check couldn't run at all (no call to test on).
  const [verifying, setVerifying] = useState(false)
  const [verifyFailure, setVerifyFailure] = useState(null)
  // The org default's taxonomy for the chosen name, once loaded: { uuid, options }.
  // Kept alongside the editable list so an edit can be told from the list as it
  // arrived — that difference is what decides between linking and minting.
  const [defaultTaxonomy, setDefaultTaxonomy] = useState(null)
  const [loadingDefaultOptions, setLoadingDefaultOptions] = useState(false)

  const isExtraction = fieldType === FIELD_TYPE.EXTRACTION
  const isList = fieldType === FIELD_TYPE.PICK_LIST || fieldType === FIELD_TYPE.CATEGORIZATION
  const isCategorization = fieldType === FIELD_TYPE.CATEGORIZATION
  const isVoipMetadata = fieldType === FIELD_TYPE.VOIP_METADATA
  const isLogic = fieldType === FIELD_TYPE.LOGIC
  // Both kinds of option-backed field: the caller stages these options for the
  // save path to mint a taxonomy from.
  const hasOptions = isList || (isVoipMetadata && voipPicklist)

  // Whether the field would resolve against the org default's own options: they
  // loaded, and nothing has been changed since. An edit means this configuration
  // wants its own list, so the adoption drops and the save mints from the edits.
  const adoptedTaxonomyUuid =
    !isNil(defaultTaxonomy) && isEqual(options, defaultTaxonomy.options)
      ? defaultTaxonomy.uuid
      : null

  useEffect(() => {
    const uuid = hasOptions
      ? defaultFieldsByFieldName[normalizeFieldKey(name)]?.taxonomy_uuid
      : undefined
    if (isNil(uuid)) {
      setDefaultTaxonomy(null)
      return undefined
    }
    if (uuid === defaultTaxonomy?.uuid) {
      return undefined
    }

    let active = true
    setLoadingDefaultOptions(true)
    dispatch(fetchTaxonomyOptions(uuid))
      .then((result) => {
        if (!active) {
          return
        }
        const defaultOptions = result.options.map((option) => ({
          name: option.name,
          description: option.description ?? '',
        }))
        setDefaultTaxonomy({ uuid, options: defaultOptions })
        // Never over an in-progress list: options typed before the name was
        // chosen are the user's own, and keeping them leaves the adoption off.
        if (isEmpty(options)) {
          setOptions(defaultOptions)
        }
      })
      .catch((err) => {
        // Without the default's options there's nothing to adopt — the field
        // falls back to a list of its own, which is the pre-existing behavior.
        console.error(err)
        if (active) {
          setDefaultTaxonomy(null)
        }
      })
      .finally(() => {
        if (active) {
          setLoadingDefaultOptions(false)
        }
      })

    return () => {
      active = false
    }
  }, [name, hasOptions])

  // A field's type is fixed once created, so switching types clears a chosen
  // existing name whose type no longer matches (its option is disabled in the
  // dropdown too).
  const selectType = (nextFieldType, nextDataType, nextVoipPicklist = false) => {
    // A verification failure belongs to the config that produced it — changing
    // any part of that config means the next Add verifies afresh instead of
    // being waved through as an already-acknowledged failure.
    setVerifyFailure(null)
    // The description holds a metadata key on a voip_metadata field and prose
    // everywhere else, so crossing that boundary clears it instead of carrying
    // one meaning's text into the other.
    if ((nextFieldType === FIELD_TYPE.VOIP_METADATA) !== isVoipMetadata) {
      setDescription('')
    }
    // Likewise a logic field's description is its combinator, so it starts at
    // "all" and goes back to blank on the way out.
    if ((nextFieldType === FIELD_TYPE.LOGIC) !== isLogic) {
      setDescription(nextFieldType === FIELD_TYPE.LOGIC ? LOGIC_COMBINATOR.ALL : '')
    }
    // Options only belong to a voip_metadata field via its Pick List data type.
    setVoipPicklist(nextFieldType === FIELD_TYPE.VOIP_METADATA && nextVoipPicklist)
    setFieldType(nextFieldType)
    setDataType(nextDataType)
    const chosen = fieldNameOptions.find((option) => option.value === name)
    if (
      chosen &&
      !isNil(chosen.displayType) &&
      resolveDisplayTypeKey(chosen.displayType) !==
        resolveSelectedTypeKey(nextFieldType, nextDataType, nextVoipPicklist)
    ) {
      setName('')
    }
  }

  const reset = () => {
    verifyRunRef.current += 1
    setFieldType(FIELD_TYPE.EXTRACTION)
    setDataType(DATA_TYPE.STRING)
    setName('')
    setDescription('')
    setOptions([])
    setVoipPicklist(false)
    setVerifying(false)
    setVerifyFailure(null)
    setDefaultTaxonomy(null)
  }

  // Any edit to the config invalidates a failure the user has acknowledged, so
  // the next Add re-checks rather than being waved through on a stale result.
  //
  // A name that already exists as an org default arrives with that default's
  // shape: its type (which is fixed by the existing field, not a choice) and its
  // description, so the field starts from what the organization already says
  // rather than from blank. The description only fills a blank box — text already
  // typed is the user's own.
  const editName = (nextName) => {
    setVerifyFailure(null)
    setName(nextName)

    const defaultField = defaultFieldsByFieldName[normalizeFieldKey(nextName)]
    if (isNil(defaultField)) {
      return
    }
    const controls = resolveDefaultFieldControls(defaultField)
    setFieldType(controls.fieldType)
    setDataType(controls.dataType)
    setVoipPicklist(controls.voipPicklist)
    // A combinator isn't text the user typed, so crossing into or out of a logic
    // field takes the default's description outright.
    if ((controls.fieldType === FIELD_TYPE.LOGIC) !== isLogic) {
      setDescription(
        defaultField.description ||
          (controls.fieldType === FIELD_TYPE.LOGIC ? LOGIC_COMBINATOR.ALL : '')
      )
      return
    }
    if (isEmpty(description.trim()) && !isEmpty(defaultField.description)) {
      setDescription(defaultField.description)
    }
  }

  const editOptions = (nextOptions) => {
    setVerifyFailure(null)
    setOptions(nextOptions)
  }

  // Picking a voip_metadata data type keeps the field type and flips whether it's
  // option-backed; Pick List resolves against options and stays a string.
  const selectVoipDataType = (nextDataType, picklist = false) =>
    selectType(FIELD_TYPE.VOIP_METADATA, nextDataType, picklist)

  // Existing names carry their own type; one that resolves to a different UI
  // type than the chosen one can't be reused (types are immutable). Free-text
  // and the current value have no displayType, so they're never blocked.
  const selectedTypeKey = resolveSelectedTypeKey(fieldType, dataType, voipPicklist)
  const isTypeMismatch = (option) =>
    !isNil(option.displayType) && resolveDisplayTypeKey(option.displayType) !== selectedTypeKey

  const formatFieldNameOption = (option, meta) => {
    const mismatch = isTypeMismatch(option)
    const label = (
      <span
        className={classNames('add-field__name-option', {
          'add-field__name-option--disabled': mismatch,
        })}
      >
        <span className="add-field__name-option-label">{option.label}</span>
        <FieldTypePill displayType={option.displayType} />
      </span>
    )
    if (meta.context === 'menu' && mismatch) {
      return (
        <InteractiveTooltip
          block
          placement="right"
          content={`Field is a ${getDisplayTypeLabel(option.displayType)} type already, please choose a different name`} // prettier-ignore
        >
          {label}
        </InteractiveTooltip>
      )
    }
    return label
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  // A name already used elsewhere in the configuration being edited (a playbook
  // flow, or the org defaults) can't be reused — matches the row editor's
  // per-configuration uniqueness, case/space-insensitive.
  const isDuplicateName = !isEmpty(name.trim()) && usedFieldNames.includes(normalizeFieldKey(name))

  // Why "Add Field" is disabled, or '' when it's ready — checked most-blocking
  // first and surfaced as the button's disabled tooltip.
  const getDisabledReason = () => {
    if (isEmpty(name.trim())) {
      return 'Enter a field name'
    }
    if (isDuplicateName) {
      return 'That name is already used in this configuration'
    }
    if (isVoipMetadata && isEmpty(description.trim())) {
      return 'Enter a metadata field'
    }
    if (!hasOptions) {
      return ''
    }
    if (loadingDefaultOptions) {
      return 'Loading your organization default options'
    }
    // The adopted list is already a saved taxonomy, so it stands on its own.
    if (!isNil(adoptedTaxonomyUuid)) {
      return ''
    }
    return getOptionsError(options, isCategorization)
  }

  const disabledReason = getDisabledReason()
  const addDisabled = !isEmpty(disabledReason)

  // A metadata field is dry-run against the playbook's most recent call before
  // it's added. It takes a playbook to check against, so the org-defaults editor
  // (no playbook) adds without verifying.
  const canVerify = isVoipMetadata && !isEmpty(playbookCid)

  // The confirm button says what the click will actually do: run the check first
  // for a metadata field, or go straight in once its failure has been seen.
  const getConfirmLabel = () => {
    if (!isNil(verifyFailure)) {
      return 'Add Anyway'
    }
    if (canVerify) {
      return 'Test Configuration and Save'
    }
    return 'Add Field'
  }
  const confirmLabel = getConfirmLabel()

  // Verify first, then add. A failure is reported instead of added — but it's
  // only advisory: the error stays on screen and the next click (now "Add
  // Anyway") adds the field as configured. Both outcomes name the call the config
  // ran against, so they can go look at it.
  const handleAdd = async () => {
    if (addDisabled || verifying) {
      return
    }
    if (canVerify && isNil(verifyFailure)) {
      setVerifying(true)
      verifyRunRef.current += 1
      const runId = verifyRunRef.current
      // A brand-new option-backed field has no taxonomy yet (its options are
      // staged until save), so this verifies the key and type only.
      const { verified, error, callId } = await dispatch(
        verifyMetadataField({
          fieldName: name,
          dataType,
          metadataKey: description,
          playbookCid,
          organizationId,
        })
      )
      // Closed (or reset) while the check was in flight — its result no longer
      // describes what's on screen.
      if (verifyRunRef.current !== runId) {
        return
      }
      setVerifying(false)
      if (!verified) {
        setVerifyFailure({ error, callId })
        return
      }
      toast.success(`Configuration verified on call ${callId}`)
    }
    onCreate({
      key: name.trim(),
      value: description.trim(),
      fieldType,
      dataType,
      taxonomyUuid: adoptedTaxonomyUuid,
      // Nothing to stage when the field links to a taxonomy that already exists.
      options:
        hasOptions && isNil(adoptedTaxonomyUuid)
          ? options.map((option) => ({
              name: option.name.trim(),
              description: (option.description ?? '').trim(),
            }))
          : [],
    })
    reset()
  }

  return (
    <BasicModal show={show} onClose={handleClose} title="Add Field" size="tiny" scrolling>
      <div className="add-field">
        <div className="add-field__section">
          <label className="add-field__label">Type</label>
          <SegmentedControl
            items={[
              {
                accessor: DATA_TYPE.STRING,
                label: 'Text',
                active: isExtraction && dataType === DATA_TYPE.STRING,
                onClick: () => selectType(FIELD_TYPE.EXTRACTION, DATA_TYPE.STRING),
                dataTestId: 'add-field-type-text',
                tooltip: TEXT_TOOLTIP,
              },
              {
                accessor: DATA_TYPE.INT,
                label: 'Number',
                active: isExtraction && dataType === DATA_TYPE.INT,
                onClick: () => selectType(FIELD_TYPE.EXTRACTION, DATA_TYPE.INT),
                dataTestId: 'add-field-type-number',
                tooltip: NUMBER_TOOLTIP,
              },
              {
                accessor: DATA_TYPE.BOOL,
                label: 'Yes/No',
                active: isExtraction && dataType === DATA_TYPE.BOOL,
                onClick: () => selectType(FIELD_TYPE.EXTRACTION, DATA_TYPE.BOOL),
                dataTestId: 'add-field-type-yesno',
                tooltip: YESNO_TOOLTIP,
              },
              {
                accessor: 'list',
                label: 'Pick List',
                active: isList,
                onClick: () => selectType(FIELD_TYPE.PICK_LIST, DATA_TYPE.STRING),
                dataTestId: 'add-field-type-list',
                tooltip: 'Select a single value from a list of options. Allows for filtering by values.', // prettier-ignore
              },
              {
                accessor: FIELD_TYPE.VOIP_METADATA,
                label: 'VoIP Metadata',
                active: isVoipMetadata,
                onClick: () => selectType(FIELD_TYPE.VOIP_METADATA, DATA_TYPE.STRING),
                dataTestId: 'add-field-type-voip-metadata',
                tooltip: VOIP_METADATA_TOOLTIP,
              },
              // Still shown when a reused name made this a logic field, so the
              // active type is never one the control can't display.
              ...(allowLogic || isLogic
                ? [
                    {
                      accessor: FIELD_TYPE.LOGIC,
                      label: 'Logic',
                      active: isLogic,
                      onClick: () => selectType(FIELD_TYPE.LOGIC, DATA_TYPE.BOOL),
                      dataTestId: 'add-field-type-logic',
                      tooltip: LOGIC_TOOLTIP,
                    },
                  ]
                : []),
            ]}
          />
        </div>

        {isVoipMetadata && (
          <div className="add-field__section">
            <label className="add-field__label">Data type</label>
            <SegmentedControl
              items={[
                {
                  accessor: DATA_TYPE.STRING,
                  label: 'Text',
                  active: dataType === DATA_TYPE.STRING && !voipPicklist,
                  onClick: () => selectVoipDataType(DATA_TYPE.STRING),
                  dataTestId: 'add-field-voip-data-type-text',
                  tooltip: 'Use the metadata value as-is.',
                },
                {
                  accessor: DATA_TYPE.FLOAT,
                  label: 'Number',
                  active: dataType === DATA_TYPE.FLOAT,
                  onClick: () => selectVoipDataType(DATA_TYPE.FLOAT),
                  dataTestId: 'add-field-voip-data-type-number',
                  tooltip: 'The metadata value must be a number, whole or fractional.',
                },
                {
                  accessor: DATA_TYPE.BOOL,
                  label: 'Yes/No',
                  active: dataType === DATA_TYPE.BOOL,
                  onClick: () => selectVoipDataType(DATA_TYPE.BOOL),
                  dataTestId: 'add-field-voip-data-type-yesno',
                  tooltip: 'The metadata value must be true or false.',
                },
                {
                  accessor: 'picklist',
                  label: 'Pick List',
                  active: voipPicklist,
                  onClick: () => selectVoipDataType(DATA_TYPE.STRING, true),
                  dataTestId: 'add-field-voip-data-type-picklist',
                  tooltip: 'The metadata value is one of a list of options you define below.', // prettier-ignore
                },
              ]}
            />
          </div>
        )}

        {isList && (
          <div className="add-field__section">
            <label className="add-field__label">Options type</label>
            <SegmentedControl
              items={[
                {
                  accessor: FIELD_TYPE.PICK_LIST,
                  label: `Names only (up to ${PICK_LIST_MAX_OPTIONS})`,
                  active: !isCategorization,
                  onClick: () => selectType(FIELD_TYPE.PICK_LIST, DATA_TYPE.STRING),
                  dataTestId: 'add-field-options-names',
                  tooltip: 'Good for values that will be found in the transcript.',
                },
                {
                  accessor: FIELD_TYPE.CATEGORIZATION,
                  label: `Names + descriptions (up to ${CATEGORIZATION_MAX_OPTIONS})`,
                  active: isCategorization,
                  onClick: () => selectType(FIELD_TYPE.CATEGORIZATION, DATA_TYPE.STRING),
                  dataTestId: 'add-field-options-descriptions',
                  tooltip: 'Allows for additional context for options, good for creating categories that require a description or examples to fully understand.', // prettier-ignore
                },
              ]}
            />
          </div>
        )}

        <div className="add-field__section">
          <label className="add-field__label">Field name</label>
          <div data-testid="add-field-name">
            <Select
              allowFreeText
              commitFreeTextOnBlur
              options={fieldNameOptions}
              value={name}
              onChange={(option) => editName(option?.value || '')}
              isOptionDisabled={isTypeMismatch}
              formatOptionLabel={formatFieldNameOption}
              placeholder="Select existing or enter a new name"
              aria-label="Field name"
            />
          </div>
          {isDuplicateName && (
            <ErrorMessage
              content="That name is already used in this configuration"
              data-testid="add-field-name-error"
            />
          )}
          {!isDuplicateName && !isNil(defaultFieldsByFieldName[normalizeFieldKey(name)]) && (
            <p className="muted-text" data-testid="add-field-default-name-hint">
              This name already exists in your organization defaults, so its type and description
              come from there.
            </p>
          )}
        </div>

        {/* A voip_metadata field is configured by name + the metadata key it
            reads, and a logic field by name + how it combines its conditions;
            neither runs a prompt, so neither has a description to write. */}
        {isLogic && (
          <div className="add-field__section">
            <label className="add-field__label">Combine conditions with</label>
            <LogicCombinator
              value={description}
              onChange={setDescription}
              testIdPrefix="add-field-logic"
            />
            <p className="muted-text" data-testid="add-field-logic-hint">
              {LOGIC_CONDITIONS_HINT}
            </p>
          </div>
        )}
        {isVoipMetadata && (
          <div className="add-field__section">
            <label className="add-field__label" htmlFor="add-field-metadata-key">
              Metadata field
              <InteractiveTooltip content={VOIP_METADATA_KEY_HINT} placement="top">
                <IconInfoCircle
                  size={16}
                  className="add-field__info-icon"
                  data-testid="add-field-metadata-key-hint"
                />
              </InteractiveTooltip>
            </label>
            <TextInput
              id="add-field-metadata-key"
              value={description}
              onChange={(event) => {
                setVerifyFailure(null)
                setDescription(event.target.value)
              }}
              placeholder="e.g. caller.region"
              data-testid="add-field-metadata-key"
            />
          </div>
        )}
        {!isVoipMetadata && !isLogic && (
          <div className="add-field__section">
            <label className="add-field__label" htmlFor="add-field-description">
              Description
              {DESCRIPTION_HINT[dataType] && (
                <InteractiveTooltip content={DESCRIPTION_HINT[dataType]} placement="top">
                  <IconInfoCircle
                    size={16}
                    className="add-field__info-icon"
                    data-testid="add-field-description-hint"
                  />
                </InteractiveTooltip>
              )}
            </label>
            <TextArea
              id="add-field-description"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Describe the call element you want to capture"
              data-testid="add-field-description"
            />
          </div>
        )}

        {hasOptions && !isNil(defaultTaxonomy) && (
          <p className="muted-text" data-testid="add-field-default-options-hint">
            {isNil(adoptedTaxonomyUuid)
              ? 'These options started from your organization default for this field. Because they have been edited, this field gets its own copy of them.'
              : 'These options come from your organization default for this field, and this field will use them as they are. Editing them here gives this field its own copy instead.'}
          </p>
        )}
        {hasOptions && (
          <OptionsEditor
            options={options}
            onChange={editOptions}
            isCategorization={isCategorization}
            testIdPrefix="add-field"
          />
        )}
      </div>

      {/* Sits with the button that triggered the check, not with the key input
          it's about, so the outcome reads next to the action. */}
      {!isNil(verifyFailure) && (
        <ErrorMessage
          content={getVerifyErrorContent(verifyFailure)}
          data-testid="add-field-verify-error"
        />
      )}

      <div className="modal-footer">
        <Button type="button" onClick={handleClose} data-testid="add-field-cancel">
          Cancel
        </Button>
        <Button
          primary
          type="button"
          onClick={handleAdd}
          loading={verifying}
          disabled={addDisabled}
          disabledTooltip={disabledReason}
          data-testid="add-field-confirm"
        >
          {confirmLabel}
        </Button>
      </div>
    </BasicModal>
  )
}
